import { SkillEvalsConfig } from "../agent-config-options";

/**
 * Default master switch for the skill-eval harness convention. When no
 * config is supplied, the convention ships **enabled** so every skill
 * that ships an `evals/evals.json` file has a documented schema,
 * runner entry-point, and product-context injection contract.
 *
 * @see SkillEvalsConfig
 */
export const DEFAULT_SKILL_EVALS_ENABLED = true;

/**
 * Default root directory (relative to the repo root) that holds every
 * skill's SKILL.md. The harness contract says that any skill SKILL.md
 * under this root may ship an `evals/evals.json` file alongside it —
 * the runner discovers eval suites by walking
 * `<skillsRoot>/<skill-name>/evals/evals.json`.
 *
 * Defaults to `.claude/skills`, which matches the location every
 * configulator-managed project ships skills to on disk.
 *
 * @see SkillEvalsConfig
 */
export const DEFAULT_SKILL_EVALS_SKILLS_ROOT = ".claude/skills";

/**
 * Default path to the product-context fixture consumed by every eval
 * suite. Configulator ships with a `docs/src/content/docs/project-context.md`
 * file that every agent already loads at session start; the eval harness
 * re-uses that file so eval prompts are parameterised by the consuming
 * project's domain vocabulary, in-scope capabilities, and stakeholders
 * without the evals needing per-project forks.
 *
 * @see SkillEvalsConfig
 */
export const DEFAULT_PRODUCT_CONTEXT_PATH =
  "docs/src/content/docs/project-context.md";

/**
 * Default policy for whether the harness should **require** a
 * product-context file to be present before running the suite.
 *
 * `true` (default) — the runner fails fast when the file is missing,
 * because an eval that silently runs without its product-context
 * fixture is a false-positive waiting to happen.
 *
 * `false` — the runner emits a warning to stderr but still runs the
 * suite. Useful for bootstrapping a new consuming repo that has not
 * yet authored its `project-context.md`.
 *
 * @see SkillEvalsConfig
 */
export const DEFAULT_REQUIRE_PRODUCT_CONTEXT = true;

/**
 * Default for whether the convention emits the
 * `.claude/procedures/run-skill-evals.sh` helper to disk. The helper
 * is opt-in because many consumers run evals from CI or ad-hoc from
 * their own scripts rather than through the bundled harness; consumers
 * who want a ready-to-invoke runner flip this to `true`.
 *
 * @see SkillEvalsConfig
 */
export const DEFAULT_SKILL_EVALS_EMIT_RUNNER = false;

/**
 * Fully-resolved skill-evals settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedSkillEvals {
  readonly enabled: boolean;
  readonly skillsRoot: string;
  readonly productContextPath: string;
  readonly requireProductContext: boolean;
  readonly emitRunner: boolean;
}

/**
 * Resolve a (possibly absent) `SkillEvalsConfig` into a canonical
 * `ResolvedSkillEvals` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs — empty / whitespace-only or absolute `skillsRoot`,
 * empty / whitespace-only or absolute `productContextPath` — throw a
 * descriptive `Error`.
 */
export function resolveSkillEvals(
  config?: SkillEvalsConfig,
): ResolvedSkillEvals {
  const skillsRoot = config?.skillsRoot ?? DEFAULT_SKILL_EVALS_SKILLS_ROOT;
  assertValidSkillsRoot(skillsRoot);

  const productContextPath =
    config?.productContextPath ?? DEFAULT_PRODUCT_CONTEXT_PATH;
  assertValidProductContextPath(productContextPath);

  return {
    enabled: config?.enabled ?? DEFAULT_SKILL_EVALS_ENABLED,
    skillsRoot,
    productContextPath,
    requireProductContext:
      config?.requireProductContext ?? DEFAULT_REQUIRE_PRODUCT_CONTEXT,
    emitRunner: config?.emitRunner ?? DEFAULT_SKILL_EVALS_EMIT_RUNNER,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `SkillEvalsConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * convention fails the build instead of silently shipping a broken
 * eval harness. Returns the resolved config unchanged so callers can
 * write `const se = validateSkillEvalsConfig(config)` in one line.
 *
 * Malformed cases rejected here:
 *
 * - `skillsRoot` empty, whitespace-only, or absolute.
 * - `productContextPath` empty, whitespace-only, or absolute.
 */
export function validateSkillEvalsConfig(
  config?: SkillEvalsConfig,
): ResolvedSkillEvals {
  return resolveSkillEvals(config);
}

/**
 * Render the full body for the `skill-evals` rule shipped by the
 * `base` bundle. The rule documents:
 *
 * - The on-disk contract (where `evals/evals.json` lives).
 * - The JSON schema every eval file follows.
 * - The product-context injection protocol (how evals reference and
 *   interpolate the repo's `project-context.md` without forking).
 * - The runner entry-point (`run-skill-evals.sh` when opted in, or
 *   the inline `jq`-driven recipe when not).
 *
 * When the convention is disabled, the rule renders a short stub that
 * tells agents the project does not ship skill evals and that skill
 * changes ride on review alone.
 */
export function renderSkillEvalsRuleContent(se: ResolvedSkillEvals): string {
  if (!se.enabled) {
    return [
      "# Skill Evals",
      "",
      "**Skill evals are disabled in this project.** Skills do not",
      "ship `evals/evals.json` regression suites, and changes to skill",
      "prompts are caught only via review and downstream user-visible",
      "failures. There is no harness contract; the runner script and",
      "per-skill hooks are not rendered.",
      "",
      "Enable the convention via",
      "`AgentConfigOptions.skillEvals.enabled = true`.",
    ].join("\n");
  }

  const lines: Array<string> = [
    "# Skill Evals",
    "",
    "Every skill that ships under `" + se.skillsRoot + "/<skill-name>/` may",
    "carry a regression-test suite in an adjacent",
    "`evals/evals.json` file. Suites are declarative prompt/expected-output",
    "fixtures parameterised by a shared **product-context** fixture so the",
    "same eval shape works across every project that depends on",
    "configulator — each consuming repo supplies its own",
    "`project-context.md`, not its own forked eval files.",
    "",
    "Without this harness, behavioural regressions in a skill prompt are",
    "caught only when a downstream workflow breaks. Evals promote the",
    "regression check to pull-request time: a reviewer (or CI) can",
    "replay a known prompt against the updated skill and confirm the",
    "output still matches the expected shape.",
    "",
    "## On-Disk Contract",
    "",
    "```",
    "" + se.skillsRoot + "/",
    "  <skill-name>/",
    "    SKILL.md         # skill prompt (already shipped)",
    "    evals/",
    "      evals.json     # regression suite for this skill",
    "```",
    "",
    "The runner walks `" + se.skillsRoot + "` and picks up any directory",
    "containing both a `SKILL.md` and an `evals/evals.json` file. Skills",
    "without an `evals/` subdirectory are silently skipped — evals are",
    "always opt-in per skill, never required.",
    "",
    "## Eval File Schema (JSON)",
    "",
    "```json",
    "{",
    '  "skill_name": "<matches parent directory name>",',
    '  "product_context": "<path to product-context fixture, optional>",',
    '  "evals": [',
    "    {",
    '      "id": 1,',
    '      "prompt": "<user prompt sent to the skill>",',
    '      "expected_output": "<prose description of the acceptable output shape>",',
    '      "files": [],',
    '      "product_context_refs": ["<domain-vocabulary term the prompt relies on>"]',
    "    }",
    "  ]",
    "}",
    "```",
    "",
    "**Required fields:** `skill_name`, `evals`. Each `evals[]` entry",
    "requires `id`, `prompt`, and `expected_output`.",
    "",
    "**`skill_name`** must match the parent directory name exactly — the",
    "runner asserts the two agree and fails the suite on mismatch.",
    "",
    "**`product_context`** is optional and defaults to the project's",
    "configured `productContextPath` (`" +
      se.productContextPath +
      "`). Override it",
    "per-suite only when a specific skill needs a different fixture;",
    "the overwhelmingly common case is to omit the field and rely on",
    "the default.",
    "",
    "**`evals[].expected_output`** is prose, not a strict matcher. The",
    "runner surfaces both the prompt and this description to the human",
    "or model evaluating the suite — matching is a judgement call on",
    "whether the skill produced the described shape, not a byte-for-byte",
    "equality check. This keeps evals robust to acceptable prompt-",
    "output variance while still catching structural regressions.",
    "",
    "**`evals[].files`** lists additional fixture files the prompt",
    "references (meeting transcripts, existing requirement documents,",
    "etc.). Paths are relative to the repo root. The default is an",
    "empty array.",
    "",
    "**`evals[].product_context_refs`** (optional) lists the domain-",
    "vocabulary terms or sections of the product-context fixture the",
    "prompt depends on. The runner reports these alongside the prompt",
    "so an evaluator can quickly confirm the fixture still carries the",
    "referenced concepts after a `project-context.md` edit.",
    "",
    "## Product-Context Injection",
    "",
    "Every eval suite runs against the repository's",
    "`" + se.productContextPath + "` fixture, which is the same document every",
    "agent already loads at session start to frame judgement calls. The",
    "harness does **not** fork or duplicate this file — it references",
    "the canonical path so a single edit to `project-context.md`",
    "propagates to every eval suite.",
    "",
  ];

  if (se.requireProductContext) {
    lines.push(
      "The runner **requires** `" + se.productContextPath + "` to exist. A",
      "missing file fails the suite before any eval runs, because an",
      "eval that silently ran without its fixture would produce a",
      "false-positive pass.",
      "",
    );
  } else {
    lines.push(
      "The runner does **not** require `" + se.productContextPath + "` to",
      "exist. A missing file emits a warning to stderr but the suite",
      "continues — useful for bootstrapping a repo that has not yet",
      "authored its product-context fixture.",
      "",
    );
  }

  lines.push(
    "When authoring a new eval, always cite the specific terms from",
    "`project-context.md` the prompt leans on via the optional",
    "`product_context_refs` array. An eval that does not declare its",
    "refs silently drifts out of sync with the fixture; a declaring",
    "eval can be grep-checked after any `project-context.md` edit.",
    "",
    "## Runner Contract",
    "",
    "Every eval runner — whether the bundled helper, a CI workflow, or",
    "a hand-rolled script — follows the same contract:",
    "",
    "1. **Discover.** Walk `" + se.skillsRoot + "` and collect every",
    "   `<skill>/evals/evals.json` file.",
    "2. **Validate.** Parse each file as JSON, assert `skill_name`",
    "   matches the parent directory, assert every `evals[]` entry has",
    "   `id`, `prompt`, and `expected_output`.",
    "3. **Load the product-context.** Read the per-suite",
    "   `product_context` path (or fall back to the project default",
    "   `" + se.productContextPath + "`).",
  );

  if (se.requireProductContext) {
    lines.push("   Fail the suite when the file is missing.");
  } else {
    lines.push("   Warn on stderr but continue when the file is missing.");
  }

  lines.push(
    "4. **Emit a plan.** Print a JSON execution plan to stdout — one",
    "   entry per `(skill, eval)` pair with the prompt, expected",
    "   output, resolved product-context path, and list of",
    "   `product_context_refs`. The plan is consumed by whatever human",
    "   or model actually runs the prompts against the skill; the",
    "   harness itself never invokes an LLM.",
    "5. **Exit non-zero on any validation failure.** Schema violations,",
    "   `skill_name` / directory mismatch, malformed JSON, and (when",
    "   required) missing product-context all fail the suite.",
    "",
    "Keeping the harness declarative means every configulator consumer",
    "runs the same contract whether the actual LLM dispatch happens in",
    "CI via the Anthropic SDK, in a hosted eval platform, or from an",
    "interactive `claude-code` session replaying prompts by hand. The",
    "harness is a schema validator and discovery layer — not an LLM",
    "runtime.",
    "",
  );

  if (se.emitRunner) {
    lines.push(
      "## Bundled Runner",
      "",
      "The `.claude/procedures/run-skill-evals.sh` helper implements",
      "the full runner contract in one invocation:",
      "",
      "```bash",
      ".claude/procedures/run-skill-evals.sh                 # run every suite",
      ".claude/procedures/run-skill-evals.sh <skill-name>    # run one suite",
      "```",
      "",
      "The helper depends on `jq` (for JSON parsing) and writes the",
      "execution plan to stdout. It exits non-zero with a descriptive",
      "diagnostic on any validation failure, so it plugs directly into",
      "pre-push hooks and CI jobs without further glue.",
      "",
    );
  } else {
    lines.push(
      "## Inline Runner Recipe",
      "",
      "Projects that prefer to roll their own runner can implement the",
      "contract with `jq` and `find`:",
      "",
      "```bash",
      "find " + se.skillsRoot + " -path '*/evals/evals.json' -print0 \\",
      "  | while IFS= read -r -d '' eval_file; do",
      '      jq -e ".skill_name, .evals[0].id, .evals[0].prompt, .evals[0].expected_output" "$eval_file" > /dev/null \\',
      '        || { echo "ERROR: malformed $eval_file"; exit 1; }',
      "    done",
      "```",
      "",
      "Flip `skillEvals.emitRunner = true` in `AgentConfigOptions` to",
      "have configulator write `.claude/procedures/run-skill-evals.sh`",
      "into the repo automatically.",
      "",
    );
  }

  lines.push(
    "## Authoring Guidelines",
    "",
    "1. **Ship evals with the skill change.** When you update a",
    "   SKILL.md prompt in a way that could change output shape, add",
    "   or update at least one eval in the same commit so the",
    "   regression is caught on the next suite run.",
    "2. **Keep prompts realistic.** An eval prompt should look like",
    "   something a user would actually type or paste into the skill,",
    "   not a synthetic stress test.",
    "3. **Describe, do not assert.** Write `expected_output` as prose",
    "   that a human or model can use to judge the actual output,",
    "   not a regex or exact string. Byte-for-byte matching is too",
    "   brittle for skill evals.",
    "4. **Cover the representative branches.** Two or three evals per",
    "   skill is usually enough — one happy path, one branch that",
    "   defers a decision (if the skill has a decision-authority",
    "   rule), and one that draws on product-context vocabulary.",
    "5. **Declare product-context refs.** Any term the prompt leans on",
    "   from `project-context.md` belongs in `product_context_refs`.",
    "   A grep-able ref list is the only sustainable way to catch",
    "   evals that drift out of sync with the fixture.",
  );

  return lines.join("\n");
}

/**
 * Render the short skill-evals hook section injected into a skill's
 * owning bundle rule (requirements-writer, bcm-writer, etc.). The
 * section cites the full contract documented in the base bundle's
 * `skill-evals` rule so individual bundles stay DRY.
 *
 * When the convention is disabled, the function returns an empty
 * string so callers can no-op their append path.
 */
export function renderSkillEvalsBundleHook(
  se: ResolvedSkillEvals,
  skillLabel: string,
): string {
  if (!se.enabled) {
    return "";
  }

  return [
    "## Skill Evals",
    "",
    `The ${skillLabel} skill ships a regression suite at`,
    `\`${se.skillsRoot}/${skillLabel}/evals/evals.json\` when one exists.`,
    "When updating the skill prompt in a way that could change output",
    "shape, add or update at least one eval in the same commit. The",
    "harness is parameterised by the repository's",
    `\`${se.productContextPath}\` fixture — prompts that lean on domain`,
    "vocabulary declare the referenced terms via",
    "`product_context_refs` so the suite can be cross-checked after any",
    "edit to the product-context file. See the `skill-evals` rule for",
    "the full JSON schema, runner contract, and authoring guidelines.",
  ].join("\n");
}

/**
 * Render the `.claude/procedures/run-skill-evals.sh` helper script.
 * Exported so `AgentConfig` can register it as an `AgentProcedure`
 * when the consumer opts in via `emitRunner: true`.
 *
 * The script takes zero or one positional arguments:
 *
 * 1. `[<skill-name>]` — optional, restricts the run to one skill.
 *
 * It exits non-zero on any of the following:
 *
 * - `jq` is not available on `PATH`.
 * - A discovered `evals.json` is malformed or missing required fields.
 * - `skill_name` inside the file does not match the parent directory.
 * - The product-context fixture is missing and `requireProductContext`
 *   is `true` in the resolved config.
 */
export function renderSkillEvalsRunnerScript(se: ResolvedSkillEvals): string {
  const requireProductContextLiteral = se.requireProductContext
    ? "true"
    : "false";

  return [
    "#!/usr/bin/env bash",
    "# run-skill-evals.sh — Discover and validate skill eval suites.",
    "#",
    "# Usage:",
    "#   .claude/procedures/run-skill-evals.sh            # run every suite",
    "#   .claude/procedures/run-skill-evals.sh <skill>    # run one suite",
    "#",
    "# Walks the skills root, discovers `<skill>/evals/evals.json`",
    "# files, validates the JSON against the skill-evals schema, asserts",
    "# the product-context fixture is reachable, and prints a JSON",
    "# execution plan to stdout. The plan is consumed by whatever human",
    "# or model actually replays the prompts against the skill — this",
    "# helper does not invoke an LLM.",
    "#",
    "# This helper implements the runner contract documented by the",
    "# skill-evals rule.",
    "",
    "set -uo pipefail",
    "",
    'SKILLS_ROOT="' + se.skillsRoot + '"',
    'PRODUCT_CONTEXT_PATH="' + se.productContextPath + '"',
    'REQUIRE_PRODUCT_CONTEXT="' + requireProductContextLiteral + '"',
    "",
    "err() {",
    '  printf "run-skill-evals.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "if ! command -v jq >/dev/null 2>&1; then",
    '  err "jq is required on PATH"',
    "  exit 2",
    "fi",
    "",
    'target_skill="${1:-}"',
    "",
    "# Resolve the product-context file. When it is missing and the",
    "# convention requires it, fail the suite. When the convention",
    "# permits it to be absent, warn and continue.",
    'if [[ ! -f "$PRODUCT_CONTEXT_PATH" ]]; then',
    '  if [[ "$REQUIRE_PRODUCT_CONTEXT" == "true" ]]; then',
    '    err "product-context fixture not found at $PRODUCT_CONTEXT_PATH"',
    "    exit 3",
    "  else",
    '    err "warning: product-context fixture not found at $PRODUCT_CONTEXT_PATH (continuing)"',
    "  fi",
    "fi",
    "",
    "# Collect every eval suite on disk. The find pattern matches both",
    "# `evals/evals.json` and `evals.json` in case a skill author inlines",
    "# the file without the subdirectory (supported but not recommended).",
    "suites=()",
    'while IFS= read -r -d "" f; do',
    '  suites+=("$f")',
    'done < <(find "$SKILLS_ROOT" -type f \\( -path "*/evals/evals.json" -o -path "*/evals.json" \\) -print0 2>/dev/null)',
    "",
    "if [[ ${#suites[@]} -eq 0 ]]; then",
    '  err "no eval suites found under $SKILLS_ROOT"',
    "  exit 0",
    "fi",
    "",
    "failed=0",
    "plan_entries=()",
    "",
    'for eval_file in "${suites[@]}"; do',
    '  skill_dir="$(dirname "$(dirname "$eval_file")")"',
    '  skill_name_from_dir="$(basename "$skill_dir")"',
    "",
    "  # Skip when the caller asked for a specific skill and this is not it.",
    '  if [[ -n "$target_skill" && "$target_skill" != "$skill_name_from_dir" ]]; then',
    "    continue",
    "  fi",
    "",
    '  if ! declared_name=$(jq -re ".skill_name" "$eval_file" 2>/dev/null); then',
    '    err "malformed eval file: $eval_file (missing skill_name)"',
    "    failed=$((failed + 1))",
    "    continue",
    "  fi",
    "",
    '  if [[ "$declared_name" != "$skill_name_from_dir" ]]; then',
    "    err \"$eval_file: skill_name '$declared_name' does not match directory '$skill_name_from_dir'\"",
    "    failed=$((failed + 1))",
    "    continue",
    "  fi",
    "",
    "  # Validate every eval entry has id, prompt, and expected_output.",
    '  if ! jq -e \'.evals | type == "array" and length > 0 and all(.[]; has("id") and has("prompt") and has("expected_output"))\' "$eval_file" > /dev/null 2>&1; then',
    '    err "$eval_file: evals array is missing or entries are missing required fields"',
    "    failed=$((failed + 1))",
    "    continue",
    "  fi",
    "",
    "  # Compute the resolved product-context path (per-suite override",
    "  # wins over the project default).",
    '  suite_context=$(jq -r ".product_context // empty" "$eval_file")',
    '  if [[ -z "$suite_context" ]]; then',
    '    suite_context="$PRODUCT_CONTEXT_PATH"',
    "  fi",
    "",
    '  plan_entries+=("$(jq -n \\',
    '    --arg skill "$skill_name_from_dir" \\',
    '    --arg file "$eval_file" \\',
    '    --arg ctx "$suite_context" \\',
    '    --slurpfile body "$eval_file" \\',
    "    '{skill: $skill, file: $file, product_context: $ctx, evals: $body[0].evals}')\")",
    "done",
    "",
    "if [[ $failed -gt 0 ]]; then",
    '  err "$failed eval suite(s) failed validation"',
    "  exit 4",
    "fi",
    "",
    "if [[ ${#plan_entries[@]} -eq 0 ]]; then",
    '  err "no matching eval suites"',
    "  exit 0",
    "fi",
    "",
    "# Emit the execution plan as a single JSON array on stdout.",
    'printf "%s\\n" "${plan_entries[@]}" | jq -s \'.\'',
    "",
    "exit 0",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function assertValidSkillsRoot(value: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `SkillEvalsConfig.skillsRoot must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.startsWith("/")) {
    throw new Error(
      `SkillEvalsConfig.skillsRoot must be a repo-relative path (no leading '/'); got ${JSON.stringify(
        value,
      )}`,
    );
  }
}

function assertValidProductContextPath(value: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `SkillEvalsConfig.productContextPath must be a non-empty string; got ${JSON.stringify(
        value,
      )}`,
    );
  }
  if (value.startsWith("/")) {
    throw new Error(
      `SkillEvalsConfig.productContextPath must be a repo-relative path (no leading '/'); got ${JSON.stringify(
        value,
      )}`,
    );
  }
}
